import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, Mail } from "lucide-react";
import type { AssignableUser, UpdateClaimAccessRequestBlockedModulesEnum } from "@/api/generated/client/models";
import { CLAIM_MODULE_OPTIONS } from "@/constants/claim-options";

interface InviteUsersDialogProps {
    open: boolean;
    canWrite: boolean;
    isAssigning: boolean;
    isInvitingByEmail: boolean;
    isLoadingAssignable: boolean;
    assignableUsers: AssignableUser[];
    selectedAssignableUserId: string;
    inviteEmail: string;
    shareLevel: "ro" | "rw";
    hiddenModules: UpdateClaimAccessRequestBlockedModulesEnum[];
    onSetSelectedAssignableUserId: (value: string) => void;
    onSetInviteEmail: (value: string) => void;
    onSetShareLevel: (value: "ro" | "rw") => void;
    onToggleHiddenModule: (module: UpdateClaimAccessRequestBlockedModulesEnum, checked: boolean) => void;
    onInvite: () => Promise<void>;
    onRequestClose: () => void;
    onOpen: () => void;
    afterInvite?: () => void;
}

export function InviteUsersDialog({
    open,
    canWrite,
    isAssigning,
    isInvitingByEmail,
    isLoadingAssignable,
    assignableUsers,
    selectedAssignableUserId,
    inviteEmail,
    shareLevel,
    hiddenModules,
    onSetSelectedAssignableUserId,
    onSetInviteEmail,
    onSetShareLevel,
    onToggleHiddenModule,
    onInvite,
    onRequestClose,
    onOpen,
    afterInvite,
}: InviteUsersDialogProps) {
    const handleInvite = async () => {
        await onInvite();
        if (afterInvite) {
            afterInvite();
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (nextOpen) {
                    onOpen();
                    return;
                }
                onRequestClose();
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Invite Users</DialogTitle>
                    <DialogDescription>
                        Choose a teammate or enter an email, then set access and module permissions.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 pt-1">
                    <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Team member</p>
                        <Select
                            value={selectedAssignableUserId}
                            onValueChange={(value) => {
                                onSetSelectedAssignableUserId(value);
                                if (value) {
                                    onSetInviteEmail("");
                                }
                            }}
                            disabled={
                                !canWrite ||
                                isAssigning ||
                                isLoadingAssignable ||
                                assignableUsers.length === 0 ||
                                inviteEmail.trim().length > 0
                            }
                        >
                            <SelectTrigger className="h-9 text-sm">
                                <SelectValue
                                    placeholder={
                                        isLoadingAssignable
                                            ? "Loading teammates..."
                                            : assignableUsers.length === 0
                                                ? "All teammates are already assigned"
                                                : "Select a teammate"
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {assignableUsers.map((user) => (
                                    <SelectItem key={user.id} value={String(user.id)}>
                                        {user.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Email address</p>
                        <Input
                            type="email"
                            placeholder="user@company.com"
                            value={inviteEmail}
                            onChange={(event) => {
                                const value = event.target.value;
                                onSetInviteEmail(value);
                                if (value.trim().length > 0 && selectedAssignableUserId) {
                                    onSetSelectedAssignableUserId("");
                                }
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    void handleInvite();
                                }
                            }}
                            className="h-9 text-sm"
                            disabled={!canWrite || isInvitingByEmail || selectedAssignableUserId.length > 0}
                        />
                    </div>

                    <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Access level</p>
                        <Select
                            value={shareLevel}
                            onValueChange={(value: "ro" | "rw") => onSetShareLevel(value)}
                            disabled={!canWrite || isInvitingByEmail || isAssigning}
                        >
                            <SelectTrigger className="h-9 text-sm">
                                <SelectValue placeholder="Access level" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="rw">Can edit</SelectItem>
                                <SelectItem value="ro">Read only</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Visible modules</p>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 rounded-md border bg-muted/20 p-2">
                            {CLAIM_MODULE_OPTIONS.map((moduleOption) => {
                                const isVisible = !hiddenModules.includes(moduleOption.value);
                                return (
                                    <label
                                        key={moduleOption.value}
                                        className="flex items-center gap-1.5 text-xs text-muted-foreground"
                                    >
                                        <Checkbox
                                            checked={isVisible}
                                            onCheckedChange={(checked) =>
                                                onToggleHiddenModule(moduleOption.value, checked !== true)
                                            }
                                            disabled={!canWrite || isInvitingByEmail || isAssigning}
                                            className="h-3.5 w-3.5"
                                        />
                                        <span>{moduleOption.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onRequestClose}
                        disabled={isInvitingByEmail || isAssigning}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => void handleInvite()}
                        disabled={
                            !canWrite ||
                            isInvitingByEmail ||
                            isAssigning ||
                            (!selectedAssignableUserId && !inviteEmail.trim())
                        }
                    >
                        {isInvitingByEmail || isAssigning ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Mail className="h-4 w-4" />
                        )}
                        <span className="ml-1.5">Invite user</span>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
